// The Work Orders list's rows: a work order's row (rollup status, progress, techs, projection,
// live refresh), the operation rows under it and on the Operations tab, and the open WO's
// operations panel header. Moved out of app.js unchanged (docs/split-plan.md, session 6).
import { billingModal } from './billing';
import { isPaintOp, paintLine, paintName } from './bottomPaint';
import { opRowChecklistHtml } from './checklistOffice';
import { getJobData } from './laborParts';
import { patchHTML } from './liveView';
import { openNotesPanel } from './notesPanel';
import { onProjectionChange, projectionLabel, refreshProjectionSoon, workOrderProjection } from './scheduleAssist';
import { techLabel } from './techNames';
import { populateTechSelectModal, updateWeekView } from './techWeek';
import { escapeHTML, fetchIdToken, getDB, isAdminRole, toastMessage, woLabel } from './utilities';
import { aggregateWorkOrder, formatShortDate, operationSeverity, resolveAttentionSlot, resolveOperationSubline, splitTechs, techAvatar } from './wo-ops-aggregation';
import { applyFilters, applySorting } from './woFilters';

let shell = null;
export function setWoTableDeps(deps) { shell = deps; }
const paintByBoat = new Map(); // 'customer|boat' -> { text, missing }, so a redrawn row has its paint at once

/** A paint job says which can, right under its name (the boat's record arrives a moment later). */
function addOpRowPaint(cell, job) {
  if (!isPaintOp(job)) return;
  const paintEl = document.createElement('div');
  paintEl.className = 'op-row-paint';
  const title = cell.classList.contains('op-row-operation-title') ? null : cell.querySelector('.op-row-operation-title');
  if (title) title.after(paintEl); else cell.append(paintEl);
  const key = `${job.Customer_ID}|${job.Boat_ID}`;
  const fill = (el, paint) => {
    el.textContent = paint.text;
    el.classList.toggle('is-missing', paint.missing);
  };
  if (paintByBoat.has(key)) fill(paintEl, paintByBoat.get(key));
  // Rows get redrawn: fill whichever copy of this row is on screen by the time the boat arrives.
  Promise.resolve(shell.getCustomerDataById?.(job.Customer_ID, job.Boat_ID, job)).then(cb => {
    const paint = { text: paintLine(cb?.boatData), missing: !paintName(cb?.boatData) };
    paintByBoat.set(key, paint);
    fill(paintEl, paint);
    const live = document.querySelector(`.op-table-data-row[data-id="${CSS.escape(String(job.Job_ID))}"] .op-row-paint`);
    if (live) fill(live, paint);
  }).catch(() => {});
}

// An op row's Schedule date box. Chrome fires `change` on every keystroke that
// makes a valid date (typing "2" of "23" is already day 02), and the save
// redraws the list, so typed dates came out wrong. Now a click opens the
// calendar (a pick saves at once); a typed date saves on Enter or leaving the box.
function wireScheduleDateInput(input, commit) {
  let saved = input.value;
  let typing = false;
  const save = () => {
    typing = false;
    if (input.value === saved) return;
    saved = input.value;
    input.classList.toggle('schedule-date-unset', !saved);
    commit(saved);
  };
  // Live updates can change the value under us, so compare against what it was on focus.
  input.addEventListener('focus', () => { saved = input.value; });
  input.addEventListener('click', () => {
    try { input.showPicker?.(); } catch { /* not allowed here; the box still takes typing */ }
  });
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') { e.preventDefault(); save(); return; }
    if (e.key !== 'Tab') typing = true;
  });
  input.addEventListener('change', () => { if (!typing) save(); });
  input.addEventListener('blur', save);
}

// Flat-table Operations-tab row (design_handoff_wo_ops_list §3b). Single
// builder for every place an operation row is rendered — the live Operations
// list, realtime-added ops, and the closed-tab search/scroll views — so
// there is exactly one row shape to keep in sync (this replaces createJobRow
// / createNewJobRow / createClosedJobRow, which had drifted from each
// other). Keeps the .job-item class and the data-id/data-jobInfo contract
// opCheck()/updateJobRow() already rely on, and stays delegated-click-only
// (no per-row listener) since #jobList/#closedJobList already bind opCheck
// once on the parent.
export function createOperationRow(job, view) {
  const row = document.createElement('div');
  row.className = 'job-item op-table-row';

  row.setAttribute('data-id', job.Job_ID);
  row.setAttribute('data-tech', job.Techs);
  row.setAttribute('data-techId', job.Techs != '' ? (shell.getTechId(job.Techs) || '') : '');
  row.setAttribute('data-jobInfo', JSON.stringify(job));
  row.setAttribute('data-customerid', job.Customer_ID);
  row.setAttribute('data-boatid', job.Boat_ID);
  if (shell.haulStatus[job.Boat_ID]) row.setAttribute('data-hauled', shell.haulStatus[job.Boat_ID]);

  // Feeds the week-view calendar exactly as both legacy row builders did.
  if (job.Schedule_Date && job.Schedule_Date !== '') {
    shell.events.push({
      id: job.Job_ID,
      customer: job.Customer_Name,
      boat: job.Boat_Name,
      date: job.Schedule_Date,
      status: job.Status,
      tech: job.Techs,
      description: job.Description,
    });
  }

  const severity = operationSeverity(job, shell.latestReceivingData);
  row.classList.add('op-row-tint--' + severity);

  const dot = document.createElement('span');
  dot.className = 'op-row-dot op-row-dot--' + severity;
  dot.title = OP_ROW_PILL_LABEL[severity] || '';

  const opCode = document.createElement('span');
  opCode.className = 'op-row-opcode';
  opCode.textContent = job.OpCode || '';

  const operationCell = document.createElement('div');
  operationCell.className = 'op-row-operation';
  const shortDesc = (job.Short_Description_DM || job.Description || '').split(':')[0];
  const subline = resolveOperationSubline(job, shell.latestReceivingData);
  operationCell.innerHTML = `
    <div class="op-row-operation-title">${escapeHTML(shortDesc)}</div>
    <div class="op-row-operation-sub op-row-operation-sub--${severity}">${escapeHTML(subline)}</div>
    ${opRowChecklistHtml(job)}
  `;
  addOpRowPaint(operationCell, job);

  const notesBtn = document.createElement('button');
  notesBtn.type = 'button';
  notesBtn.className = 'job-notes-button op-row-notes-btn';
  notesBtn.innerHTML = `<img src="/assets/note-icon.png" alt="Notes" width="16" height="16">`;
  if (job.Notes) {
    notesBtn.querySelector('img').style.filter = 'brightness(0) saturate(100%) invert(15%) sepia(95%) saturate(6932%) hue-rotate(359deg) brightness(94%) contrast(113%)';
  }
  notesBtn.addEventListener('click', async (e) => {
    e.stopPropagation();
    await openNotesPanel(row.getAttribute('data-id'), notesBtn);
  });
  operationCell.appendChild(notesBtn);

  const boatData = shell.latestCustomerDataById.get(job.Customer_ID)?.Boats?.[job.Boat_ID];
  const boatCell = document.createElement('div');
  boatCell.className = 'op-row-boat';
  const boatSuffix = boatData?.Boat_Length ? ` · ${boatData.Boat_Length}′ ${boatData.Boat_Make || ''}`.trimEnd() : '';
  boatCell.innerHTML = `
    <div class="op-row-boat-name">${escapeHTML(job.Boat_Name || '')}${escapeHTML(boatSuffix)}</div>
    <div class="op-row-customer-name">${escapeHTML(job.Customer_Name || '')}</div>
  `;

  const woChip = document.createElement('button');
  woChip.type = 'button';
  woChip.className = 'op-row-wo-chip';
  woChip.textContent = job.Work_Order || '';
  // A handful of older records have no Work_Order value at all — previously rendered as a visible
  // blank pill. visibility:hidden (not display:none/.hidden) so the row's CSS grid columns don't
  // shift for these rows — the chip still occupies its track, just invisible.
  if (!job.Work_Order) {
    woChip.style.visibility = 'hidden';
    woChip.tabIndex = -1;
  } else {
    woChip.addEventListener('click', (e) => {
      e.stopPropagation();
      shell.jumpToWorkOrder(job.Work_Order);
    });
  }

  const primaryTech = splitTechs(job.Techs)[0] || '';
  const techCell = document.createElement('button');
  techCell.type = 'button';
  techCell.className = 'op-row-tech op-tech-select noPrint';
  if (primaryTech) {
    const { initials, color } = techAvatar(primaryTech, shell.getTechId);
    techCell.innerHTML = `<span class="op-row-tech-avatar" style="background:${color};">${escapeHTML(initials)}</span><span class="op-row-tech-name">${escapeHTML(techLabel(primaryTech))}</span>`;
  } else {
    techCell.classList.add('op-tech-unassigned');
    techCell.innerHTML = `<span class="op-row-tech-avatar op-row-tech-avatar--empty"></span><span class="op-row-tech-name">Unassigned</span>`;
  }
  techCell.addEventListener('click', (e) => {
    e.stopPropagation();
    populateTechSelectModal(e, 'operations');
  });

  const scheduleInput = document.createElement('input');
  scheduleInput.type = 'date';
  scheduleInput.className = 'schedule-date-info op-row-schedule';
  scheduleInput.value = job.Schedule_Date ? job.Schedule_Date : '';
  if (!job.Schedule_Date || job.Schedule_Date === '') {
    scheduleInput.classList.add('schedule-date-unset');
  }
  wireScheduleDateInput(scheduleInput, (date) => {
    const jobId = scheduleInput.closest('.job-item').getAttribute('data-id');
    shell.updateJobInfo(jobId, 'Schedule_Date', date);
    shell.updateJobRow(jobId, 'Schedule_Date', date);
    applyFilters();
    applySorting();
    shell.searchCall();
    shell.searchJobs();
    updateWeekView();
  });

  const hrsCell = document.createElement('span');
  hrsCell.className = 'op-row-hrs';
  hrsCell.textContent = job.Estimated_Hours ? parseFloat(job.Estimated_Hours).toFixed(1) : '';

  const pillTone = { hold: 'red', overdue: 'red', parts: 'amber', unscheduled: 'amber', 'in-progress': 'blue', done: 'green', scheduled: 'slate' }[severity] || 'slate';
  const pillLabel = { hold: 'Hold', overdue: 'Awaiting Parts', parts: 'Awaiting Parts', unscheduled: 'Needs sched.', 'in-progress': 'In Progress', done: 'Done', scheduled: 'Scheduled' }[severity] || (job.Status || '');
  const statusPill = document.createElement('span');
  statusPill.className = `status-info-p wo-tone-${pillTone} op-row-status`;
  statusPill.textContent = pillLabel;

  row.appendChild(dot);
  row.appendChild(opCode);
  row.appendChild(operationCell);
  row.appendChild(boatCell);
  row.appendChild(woChip);
  row.appendChild(techCell);
  row.appendChild(scheduleInput);
  row.appendChild(hrsCell);
  row.appendChild(statusPill);

  if (job.Status === 'Ready to Bill' || job.Status === 'Complete') {
    row.classList.add('complete-job');
  }
  if (job.Status === 'Closed' && view !== 'closed') {
    row.classList.add('closed-job');
  }

  return row;
}

// Pure per-work-order rollup, factored out of setUpWorkOrderStatuses() so a
// single-row refresh (refreshWorkOrderRow) can compute the same status
// without re-scanning every work order in the dataset.
function computeWoRollupStatus(operations) {
  const activeOperations = operations.filter(status => status !== 'Declined');

  if (
    operations.length > 0 &&
    operations.every(status => status === 'Closed' || status === 'Declined')
  ) {
    return 'Closed';
  }
  if (
    activeOperations.length > 0 &&
    activeOperations.every(status => ['Ready to Bill', 'Complete', 'Closed'].includes(status))
  ) {
    return 'Ready to Bill';
  }
  if (
    activeOperations.length > 0 &&
    activeOperations.every(status => ['On Work Schedule', 'Estimate'].includes(status))
  ) {
    return 'Needs Scheduling';
  }
  return 'In Progress';
}

export async function setUpWorkOrderStatuses(operationsByWorkOrder) {
  try {
    let workOrderStatuses = {};

    Object.keys(operationsByWorkOrder).forEach(workOrderNumber => {
      const operations = operationsByWorkOrder[workOrderNumber] || [];
      workOrderStatuses[workOrderNumber] = {
        Work_Order: workOrderNumber,
        Status: computeWoRollupStatus(operations)
      };
    });

    return workOrderStatuses;
  } catch (error) {
    console.error('Error setting up work order statuses:', error);
  }
}


export async function renderTable(dataList, workOrderStatuses, receivingData) {
  const dataSection = document.getElementById('table-content');
  dataSection.innerHTML = ''; // Clear existing content

  // Array to hold all created rows
  const rows = [];

  const progressBar = document.getElementById('table-progress-bar');
  const eligibleCount = dataList.filter(d =>
    d['Status'] &&
    !(shell.countObject[d['Work_Order']] && shell.countObject[d['Work_Order']].Count === 0) &&
    d['Status'] !== 'Declined'
  ).length;
  let processed = 0;

  for (const data of dataList) {

    if (!data['Status'] ||
      (shell.countObject[data['Work_Order']] && shell.countObject[data['Work_Order']].Count == 0) ||
      data['Status'] === 'Declined') {
      continue;
    }

    // Await the result of createTableRow since it's an async function
    const row = await createTableRow(data, workOrderStatuses, receivingData);

    // Collect the row if it's valid
    if (row) {
      rows.push({ row, data }); // Store the row and its associated data for sorting
    }

    // Update progress bar
    processed++;
    if (progressBar && eligibleCount > 0) {
      progressBar.style.width = `${Math.round((processed / eligibleCount) * 100)}%`;
    }
  }

  // Sort the rows array based on the provided logic
  rows.sort((a, b) => {
    let aDate = a.data['Start_Date'] ? new Date(a.data['Start_Date']) : new Date(a.data['Request_Date']);
    let bDate = b.data['Start_Date'] ? new Date(b.data['Start_Date']) : new Date(b.data['Request_Date']);

    if (aDate && bDate) {
      return aDate.getTime() - bDate.getTime();
    } else if (aDate) {
      return -1;
    } else if (bDate) {
      return 1;
    } else {
      return 0;
    }
  });

  // Use a DocumentFragment to batch DOM updates
  const fragment = document.createDocumentFragment();

  // Append the sorted rows to the fragment
  rows.forEach(({ row }) => {
    fragment.appendChild(row);
  });

  document.getElementById("table-loading-icon").style.display = "none";
  const progressWrap = document.getElementById('table-progress-wrap');
  if (progressWrap) progressWrap.style.display = 'none';
  dataSection.appendChild(fragment);

  dataSection.addEventListener('click', handleRowClick);
}

function createOrphanedRow(data) {
  const row = document.createElement('div');
  row.className = 'table-item orphaned-operation';
  row.setAttribute('data-id', data['Job_ID'] || '');
  row.setAttribute('data-jobInfo', JSON.stringify(data));

  row.innerHTML = `
    <div class="orphaned-operation-content">
      <span class="orphaned-warning">⚠ Missing Work Order</span>
      <span class="orphaned-detail">${escapeHTML(data['Customer_Name'] || 'Unknown customer')} — ${escapeHTML(data['Boat_Name'] || 'Unknown boat')}</span>
      <span class="orphaned-detail">${escapeHTML(data['Description'] || data['Short_Description'] || 'No description')}</span>
      <button class="orphaned-delete-btn" data-jobid="${data['Job_ID'] || ''}">Delete Orphaned Operation</button>
    </div>
  `;

  row.querySelector('.orphaned-delete-btn').addEventListener('click', async (e) => {
    e.stopPropagation();
    const jobId = e.target.getAttribute('data-jobid');
    if (!jobId) {
      toastMessage('Cannot delete: no Job ID found.', 'red');
      return;
    }
    if (!confirm(`Delete this orphaned operation for "${data['Customer_Name'] || 'unknown customer'}"? This cannot be undone.`)) return;
    try {
      const idToken = await fetchIdToken();
      await fetch(`${shell.currentURL}/data/${jobId}.json?auth=${idToken}`, { method: 'DELETE' });
      const db = await getDB();
      const tx = db.transaction(['jobData'], 'readwrite');
      tx.objectStore('jobData').delete(jobId);
      row.remove();
      toastMessage('Orphaned operation deleted.', 'green');
    } catch (err) {
      console.error('Error deleting orphaned operation:', err);
      toastMessage('Error deleting operation: ' + err.message, 'red');
    }
  });

  return row;
}

// Maps a rolled-up Work_Order status onto the design tokens' pill tones, for
// the main Work Orders list rows. Shared by createTableRow and
// createNewTableRow (the realtime-add path) so both render identically.
export function woStatusToneSlug(status) {
  const v = (status || '').toString();
  if (v === 'In Progress') return 'blue';
  if (v === 'Ready to Bill' || v === 'Complete') return 'green';
  if (v === 'Closed') return 'slate';
  if (['Ready to Frame', 'Vacuumed', 'Framed', 'Covered', 'Tightened'].includes(v)) return 'amber';
  return 'teal'; // On Work Schedule / Needs Scheduling
}

async function createTableRow(data, workOrderStatuses, receivingData) {
  let customerData = null;
  let boatData = null;

  // The customer/boat record only feeds the optional attention hint
  // (boatData) — the row itself renders from the job's own fields. A missing
  // or not-yet-synced customer record must never hide the work order.
  try {
    const customerAndBoatData = await shell.getCustomerDataById(data['Customer_ID'], data['Boat_ID'], data);
    customerData = customerAndBoatData?.customerData || null;
    boatData = customerAndBoatData?.boatData || null;
  } catch (error) {
    console.warn('No customer record for work order row (rendering anyway):', data['Work_Order'], error);
  }

  if (!data['Work_Order']) {
    console.warn('Orphaned operation with no Work_Order — rendering delete row:', data);
    return createOrphanedRow(data);
  }

  const row = document.createElement("div");
  data.Status = workOrderStatuses[data['Work_Order']]?.Status ? workOrderStatuses[data['Work_Order']].Status : data.Status;

  row.setAttribute('data-jobInfo', JSON.stringify(data));
  row.setAttribute('data-customerId', data['Customer_ID']);
  row.setAttribute('data-boatId', data['Boat_ID']);
  row.setAttribute('data-workorder', data['Work_Order']);
  // A work order with no Op_Type on any operation (e.g. equipment work) has no entry here.
  row.setAttribute('data-optypes', JSON.stringify(shell.workOrderOpTypes[data['Work_Order']] || []));

  if (shell.haulStatus[data['Boat_ID']]) {
    row.setAttribute('data-hauled', shell.haulStatus[data['Boat_ID']]);
  }
  
  row.className = 'table-item';

  if (workOrderStatuses[data['Work_Order']].Status === 'Ready to Bill') {
    row.classList.add('complete-job');
  }

  if (workOrderStatuses[data['Work_Order']].Status === 'Closed') {
    row.classList.add('closed-job');
  }

  const statusVisual = document.createElement('div');
  statusVisual.className = 'status-info';

  const status = workOrderStatuses[data['Work_Order']].Status;
  const woTone = woStatusToneSlug(status);
  row.classList.add('wo-tone-' + woTone);

  statusVisual.innerHTML = '&nbsp;';

  const infoDiv = document.createElement('div');
  infoDiv.className = 'info-div';

  const customerDiv = document.createElement('div');
  customerDiv.className = 'job-group wo-boat-customer';
  customerDiv.innerHTML = `
    <span class="customer-name-jobs">${escapeHTML(data['Customer_Name'] || '')}</span>
    <span class="boat-name-jobs">${escapeHTML(data['Boat_Name'] || '')}</span>
    <span class="wo-number-tag">${escapeHTML(woLabel(data['Work_Order']))}</span>
  `;

  const descDiv = document.createElement('div');
  descDiv.className = 'job-group wo-description-group';
  descDiv.innerHTML = `
    <span class="work-order-description">${escapeHTML(data['Short_Description'])}</span>
    ${data['Category'] ? `<span class="wo-category-chip">${escapeHTML(data['Category'])}</span>` : ''}
  `;

  infoDiv.appendChild(customerDiv);
  infoDiv.appendChild(descDiv);

  // Progress bar + attention slot + tech avatars — replaces the bare
  // "Operations: N" count. ops/receivingData already resolved with no extra
  // fetch (jobsByWorkOrder built once in mainTable(); receivingData loaded
  // once per render pass). Factored into renderWoProgressBlock() so a
  // realtime update can refresh just this part of an existing row too
  // (see refreshWorkOrderRow()).
  const ops = (shell.jobsByWorkOrder && shell.jobsByWorkOrder.get(data['Work_Order'])) || [data];
  const rollupStatus = workOrderStatuses[data['Work_Order']].Status;
  renderWoProgressBlock(infoDiv, data, rollupStatus, ops, boatData, receivingData, woTone);

  row.appendChild(statusVisual);
  row.appendChild(infoDiv);

  return row;
}

// Builds/rebuilds the attention slot + tech avatars + status block (the
// three .job-group children after the description) inside infoDiv.
// Shared by createTableRow() (initial render) and refreshWorkOrderRow()
// (realtime patch of an already-rendered row) so the two never drift.
function renderWoProgressBlock(infoDiv, data, rollupStatus, ops, boatData, receivingData, woTone) {
  infoDiv.querySelectorAll('.wo-progress-group, .wo-attention, .wo-techs-group, .wo-status-group').forEach(el => el.remove());

  const agg = aggregateWorkOrder(ops);
  const slot = resolveAttentionSlot({ ops, receivingData, boatData, rollupStatus });

  const attentionDiv = document.createElement('div');
  attentionDiv.className = `wo-attention wo-attention--${slot.modifier}`;
  attentionDiv.innerHTML = `
    <span class="wo-attention-spine"></span>
    <div class="wo-attention-body">
      <div class="wo-attention-headline">${escapeHTML(slot.headline)}</div>
      ${slot.detail ? `<div class="wo-attention-detail">${escapeHTML(slot.detail)}</div>` : ''}
    </div>
    ${slot.showInvoice ? `<button type="button" class="wo-attention-invoice-btn dm-only">Invoice</button>` : ''}
  `;
  if (slot.showInvoice) {
    attentionDiv.querySelector('.wo-attention-invoice-btn').addEventListener('click', async (e) => {
      e.stopPropagation();
      await shell.openWoInvoicePreview(data['Work_Order'], data['Customer_ID'], data['Customer_Name'], data['Boat_Name']);
    });
  }

  const techNames = [...new Set(agg.activeOps.flatMap(op => splitTechs(op.Techs)))];
  const techsDiv = document.createElement('div');
  techsDiv.className = 'wo-techs-group';
  const avatarsHtml = techNames.slice(0, 2).map(name => {
    const { initials, color } = techAvatar(name, shell.getTechId);
    return `<span class="wo-tech-avatar" style="background:${color};">${escapeHTML(initials)}</span>`;
  }).join('');
  const extraCount = techNames.length - 2;
  techsDiv.innerHTML = `
    <div class="wo-tech-avatars">
      ${avatarsHtml}
      ${extraCount > 0 ? `<span class="wo-tech-avatar wo-tech-avatar-extra">+${extraCount}</span>` : ''}
      <span class="wo-tech-names">${escapeHTML(techNames.map(techLabel).join(', '))}</span>
    </div>
    <div class="wo-tech-next">${agg.nextScheduleDate ? `Next · ${formatShortDate(agg.nextScheduleDate)}` : ''}</div>
    <div class="wo-tech-done"></div>
  `;
  paintWoProjection(techsDiv.querySelector('.wo-tech-done'), data['Work_Order']);
  // Any row (re)render means jobs changed or just loaded — recompute, once the burst settles.
  refreshProjectionSoon();

  const statusDiv = document.createElement('div');
  statusDiv.className = 'job-group hide-mobile wo-status-group';
  const wasExpanded = infoDiv.closest('.table-item')?.classList.contains('active-work-order');
  statusDiv.innerHTML = `
    <span class="group-row">
      <span class="status-info-p wo-tone-${woTone}">${rollupStatus}</span>
    </span>
    <span class="wo-disclose">${wasExpanded ? 'Hide operations ⌃' : `${agg.totalCount} operation${agg.totalCount === 1 ? '' : 's'} ⌄`}</span>
  `;

  infoDiv.appendChild(attentionDiv);
  infoDiv.appendChild(techsDiv);
  infoDiv.appendChild(statusDiv);
}

// Projected finish ("Est. done Oct 9") from the Scheduler's engine — only shown
// once every open operation on the work order has a tech (scheduleAssist.js).
function paintWoProjection(el, workOrder) {
  if (!el) return;
  const p = workOrderProjection(workOrder);
  el.textContent = projectionLabel(workOrder);
  el.classList.toggle('is-late', !!(p && p.late));
}

onProjectionChange(() => {
  document.querySelectorAll('.table-item[data-workorder]').forEach(row => {
    paintWoProjection(row.querySelector('.wo-tech-done'), row.getAttribute('data-workorder'));
  });
});

// Re-derives rollup status + re-renders the progress/attention/techs/status
// block on an already-rendered .table-item row, from the current in-memory
// jobsByWorkOrder map. Called by every realtime/optimistic-update path that
// used to hand-patch a now-removed .operation-count span (op add/delete/
// decline, Schedule_Date/Haul_Date/Launch_Date changes) so those rows never
// throw on a missing element and always reflect the change immediately.
export async function refreshWorkOrderRow(workOrder) {
  if (!workOrder) return;
  const row = document.querySelector(`.table-item[data-workorder="${CSS.escape(String(workOrder))}"]`);
  const infoDiv = row?.querySelector('.info-div');
  const ops = shell.jobsByWorkOrder && shell.jobsByWorkOrder.get(workOrder);
  if (!row || !infoDiv || !ops || !ops.length) return;

  // Every operation on this work order is Declined — there's nothing left to
  // show. Tear the row (and its expanded operations panel, if open) down
  // instead of rendering a "0 of 0" row that has nothing left to interact
  // with. This runs before any awaited work below so it lands synchronously
  // regardless of whether the caller awaits this function.
  if (aggregateWorkOrder(ops).totalCount === 0) {
    shell.removeOperationDetails(row);
    row.remove();
    return;
  }

  const rollupStatus = computeWoRollupStatus(ops.map(op => op.Status));
  const woTone = woStatusToneSlug(rollupStatus);

  row.classList.remove('wo-tone-blue', 'wo-tone-green', 'wo-tone-slate', 'wo-tone-amber', 'wo-tone-teal', 'complete-job', 'closed-job');
  row.classList.add('wo-tone-' + woTone);
  if (rollupStatus === 'Ready to Bill') row.classList.add('complete-job');
  if (rollupStatus === 'Closed') row.classList.add('closed-job');

  const boatId = row.getAttribute('data-boatId') || row.getAttribute('data-boatid');
  const customerId = row.getAttribute('data-customerId') || row.getAttribute('data-customerid');
  let boatData = null;
  if (customerId && boatId) {
    try {
      boatData = (await shell.getCustomerDataById(customerId, boatId))?.boatData || null;
    } catch (_) { /* row still refreshes without haul/launch-wait context */ }
  }

  // Row may have been removed from the DOM while the boat lookup was in flight.
  if (!row.isConnected) return;

  const representative = { ...ops[0], Work_Order: workOrder };
  renderWoProgressBlock(infoDiv, representative, rollupStatus, ops, boatData, shell.latestReceivingData, woTone);
}

// Op-panel rebuild, Stage 9 — "other open work orders for this boat" for the
// Move dialog. jobsByWorkOrder is guaranteed populated by the time this can
// run (Move only opens from an already-open #Update panel, which only opens
// from an already-rendered row — same reasoning as the rail switcher/Stage
// 2). "Open" reuses computeWoRollupStatus() (the same rollup
// refreshWorkOrderRow() already uses for the WO row's tone) rather than a
// second, possibly-divergent definition — a WO counts as open unless its
// rollup status is 'Closed'.
export function getOpenWorkOrdersForBoat(boatId, excludeWorkOrder) {
  if (!shell.jobsByWorkOrder || !boatId) return [];
  const results = [];
  shell.jobsByWorkOrder.forEach((ops, workOrder) => {
    if (String(workOrder) === String(excludeWorkOrder)) return;
    if (!ops || !ops.some((o) => o.Boat_ID === boatId)) return;
    const rollupStatus = computeWoRollupStatus(ops.map((op) => op.Status));
    if (rollupStatus === 'Closed') return;
    const dates = ops.map((op) => op.Start_Date).filter(Boolean).sort();
    results.push({
      workOrder,
      ops,
      openedDate: dates[0] || '',
      opCount: ops.filter((op) => op.Status !== 'Declined').length,
      statusSummary: rollupStatus,
    });
  });
  return results;
}

const createdWorkOrders = new Set();

export async function createNewTableRow(data, opCount) {
  const wo = String((data && data['Work_Order']) ?? '').trim();
  if (!wo) return;
  if (createdWorkOrders.has(wo)) return;
  if (document.querySelector(`.table-item[data-workorder="${CSS.escape(wo)}"]`)) { createdWorkOrders.add(wo); return; }

  // Delegates to createTableRow so a freshly-added work order renders with
  // the exact same progress bar / attention slot / tech avatars as every
  // other row, instead of maintaining a second hand-rolled markup builder
  // that only ever drew a bare operation count.
  const singleWoStatuses = { [wo]: { Work_Order: wo, Status: data.Status } };
  const row = await createTableRow(data, singleWoStatuses, shell.latestReceivingData);
  if (!row) return;

  document.getElementById('table-content').appendChild(row);
  shell.glowJobRows([row]);
  createdWorkOrders.add(wo);
  toastMessage('New job added', 'green');
}


function handleRowClick(e) {

  const row = e.target.closest('.table-item');
  if (!row) return;

  shell.selectWorkOrder(e);
}

// Maps an operation Status onto the design tokens' pill tones — shared by
// opTableList and the single-row builder used after addOperation(), so a
// freshly-added row matches the rest of the list immediately.
export function statusToneSlug(s) {
  const v = (s || '').toString();
  if (v === 'Ready to Bill' || v === 'Complete') return 'green';
  if (v === 'Hold') return 'slate';
  if (v === 'Awaiting Approval') return 'navy';
  if (v === 'In Progress') return 'blue';
  if (v === 'Closed') return 'red';
  return 'slate';
}

// Single compact-grid row builder for the Work-Orders expand panel — shared
// by opTableList() (bulk render) and createNewOpRow() (single-op insert
// after addOperation()) so the two never drift into different row shapes
// the way createJobRow/createNewJobRow did on the Operations tab. Matches
// the same dot/opcode/title/tech/schedule/hrs/status visual language as
// createOperationRow() on the Operations tab, just without the boat/
// customer/work-order columns (redundant — we're already inside one WO).
export const OP_ROW_PILL_TONE = { hold: 'red', overdue: 'red', parts: 'amber', unscheduled: 'amber', 'in-progress': 'blue', done: 'green', scheduled: 'slate' };
export const OP_ROW_PILL_LABEL = { hold: 'Hold', overdue: 'Awaiting Parts', parts: 'Awaiting Parts', unscheduled: 'Needs sched.', 'in-progress': 'In Progress', done: 'Done', scheduled: 'Scheduled' };

// Op-panel rebuild, Stage 7c — same `.op-row-operation-sub` sub-line pattern
// createOperationRow() already uses on the Operations tab (via
// resolveOperationSubline(), now reason/date-aware — see wo-ops-
// aggregation.js), applied here only for held rows so every other row stays
// exactly as compact/single-line as the column widths were tuned for.
// Work a tech found during another operation (techs app › Found work): who found it and,
// while it waits on the customer, what's needed — a price (the Charge), or their answer
// in the portal. Customer Portal v2 shows it once it has a price.
function opRowFoundSublineHtml(op) {
  const f = op.Found_Work;
  if (!f) return '';
  const found = `Found by ${f.By || 'a tech'}${f.From_Desc ? ` during ${f.From_Desc}` : ''}`;
  let state = '';
  if (op.Status === 'Awaiting Approval') {
    state = Number(op.Charge) > 0 ? ` · with the customer to approve ($${Number(op.Charge).toFixed(2)})` : ' · needs a price — set the charge to send it to the customer';
  } else if (op.Approval && op.Approval.Status) {
    state = ` · ${op.Approval.Status === 'approved' ? 'approved' : 'declined'} by the customer in the portal`;
  }
  const tone = op.Status === 'Awaiting Approval' && !(Number(op.Charge) > 0) ? ' op-row-operation-sub--price' : '';
  return `<div class="op-row-operation-sub op-row-operation-sub--found${tone}">${escapeHTML(found + state)}</div>`;
}

function opRowHoldSublineHtml(op) {
  if (op.Status !== 'Hold') return opRowFoundSublineHtml(op);
  return `<div class="op-row-operation-sub op-row-operation-sub--hold">${escapeHTML(resolveOperationSubline(op, shell.latestReceivingData))}</div>`;
}

// Re-derives severity (dot color, row tint, status pill) on an
// already-rendered compact row. buildOpTableRow() only computes severity
// once at creation — confirmed live (via the WO expand panel) that without
// this, a row stays visually "unscheduled" (amber dot/tint) even right
// after its Schedule_Date is filled in, until the panel is closed and
// reopened. Call after mutating a field severity depends on
// (Schedule_Date/Techs/Status/Parts_Ordered) and patching data-jobInfo.
export function refreshOpRowSeverity(row, op) {
  const severity = operationSeverity(op, shell.latestReceivingData);
  row.setAttribute('data-jobInfo', JSON.stringify(op));
  row.classList.remove('op-row-done', 'op-row-running', 'op-row-unscheduled');
  if (severity === 'done') row.classList.add('op-row-done');
  else if (severity === 'in-progress') row.classList.add('op-row-running');
  else if (severity === 'unscheduled') row.classList.add('op-row-unscheduled');
  row.classList.toggle('op-row-tint--hold', op.Status === 'Hold');

  const dot = row.querySelector('.op-row-dot');
  if (dot) { dot.className = 'op-row-dot op-row-dot--' + severity; dot.title = OP_ROW_PILL_LABEL[severity] || ''; }

  const pill = row.querySelector('.op-row-status');
  if (pill) {
    pill.className = `status-info-p wo-tone-${OP_ROW_PILL_TONE[severity] || 'slate'} op-row-status`;
    pill.textContent = OP_ROW_PILL_LABEL[severity] || (op.Status || '');
  }

  const titleEl = row.querySelector('.op-row-operation-title');
  const existingSubline = titleEl && titleEl.querySelector('.op-row-hold-subline');
  if (existingSubline) existingSubline.remove();
  const sublineHtml = opRowHoldSublineHtml(op);
  if (titleEl && sublineHtml) titleEl.insertAdjacentHTML('beforeend', sublineHtml);
  // The checklist meter (checklistOffice.js) follows the op's ticks; a partial op (no Checklist key) leaves it.
  if (titleEl && 'Checklist' in op) {
    titleEl.querySelector('.op-row-checklist')?.remove();
    titleEl.insertAdjacentHTML('beforeend', opRowChecklistHtml(op));
  }
}

// "Bill Operation" row shortcut — opens the billing modal straight from the operation row instead
// of open op → More ⋯ → Billing. Shown for ops awaiting billing (Complete / Ready to Bill; a
// Closed op is already billed), and only to admins — same gate as the op panel's "ready to bill"
// banner (opPanel.js renderBillingBanner), since billing isn't a tech's job.
//
// It takes over the Status cell rather than adding a column: the row's grid has no spare width to
// give (an extra track measured live as shifting every other column ~117px left in these rows vs.
// their siblings, and would squeeze every row's Operation column to make room), while the pill it
// replaces reads "Done" for Complete, Ready to Bill and Closed alike, so nothing is lost.
//
// Kept as one add/remove function rather than baked into buildOpTableRow(): a row's status
// changes in place (Complete → Closed after billing, or back to In Progress), and the
// onChildChanged → updateOperationTableUI() path only patches the pill, so the button has to be
// re-evaluated there too or it would linger on a row that no longer needs it.
const BILLABLE_OP_STATUSES = ['Complete', 'Ready to Bill'];

export function syncOpRowBillButton(row, op) {
  const show = BILLABLE_OP_STATUSES.includes(op.Status?.toString())
    && isAdminRole(shell.userRole || localStorage.getItem('currentUserRole') || '');
  const existing = row.querySelector('.op-row-bill-btn');

  row.classList.toggle('op-row-billable', show);
  if (!show) {
    if (existing) existing.remove();
    return;
  }
  if (existing) return;

  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'op-row-bill-btn noPrint';
  btn.textContent = 'Bill Operation';
  btn.title = 'Open billing for this operation';
  // updateOperationTableUI() can hand this a partial {Status, Work_Order} (submitOpen), so fall back
  // to the code already rendered in the row.
  const opCode = op.OpCode || row.querySelector('.op-row-opcode-inline')?.textContent || '';
  btn.setAttribute('aria-label', `Bill operation ${opCode}`.trim());

  btn.addEventListener('click', async (e) => {
    // The row itself opens the op panel on click — this button exists to skip that.
    e.stopPropagation();
    if (btn.disabled) return;
    btn.disabled = true;
    try {
      // Read from IndexedDB like the More-menu path (#open-billing) does — the row's cached
      // data-jobInfo isn't refreshed when labor/parts are logged, so billing off it would show
      // stale hours and parts.
      const jobId = row.getAttribute('data-id');
      const jobInfo = (await getJobData(jobId)) || JSON.parse(row.getAttribute('data-jobInfo'));
      await billingModal(jobInfo);
    } catch (error) {
      console.error('Bill Operation: could not open billing', error);
      toastMessage('Could not open billing. Please try again.', 'red', 2500);
    } finally {
      btn.disabled = false;
    }
  });

  // Directly after the pill, so that with the pill hidden (CSS, .op-row-billable) the button is the
  // in-flow child that lands in the Status column and the notes button keeps its own column.
  const pill = row.querySelector('.op-row-status');
  if (pill) pill.after(btn); else row.appendChild(btn);
}

function buildOpTableRow(op) {
  const severity = operationSeverity(op, shell.latestReceivingData);

  const row = document.createElement('div');
  row.className = 'op-table-data-row op-row-compact';
  if (severity === 'done') row.classList.add('op-row-done');
  else if (severity === 'in-progress') row.classList.add('op-row-running');
  else if (severity === 'unscheduled') row.classList.add('op-row-unscheduled');
  if (op.Status === 'Hold') row.classList.add('op-row-tint--hold');
  row.addEventListener('click', (e) => { shell.opCheck(e); });
  row.setAttribute('data-jobInfo', JSON.stringify(op));
  row.setAttribute('data-id', op['Job_ID']);
  row.dataset.liveSig = JSON.stringify(op); // what this row was drawn from (syncOpenOperationsPanel)

  const dot = document.createElement('span');
  dot.className = 'op-row-dot op-row-dot--' + severity;
  dot.title = OP_ROW_PILL_LABEL[severity] || '';

  // Measured live against real data: giving OpCode its own grid column left
  // the title column at ~99px even on a comfortably wide window — enough for
  // maybe 12 characters before every single title ellipsized ("WINTERIZE …",
  // "PUMP HOLDI…", etc). Folding the code into the title cell as a muted
  // mono prefix reclaims that column's width for the text that actually
  // needs it, same as a lot of dense real-world tables do.
  const opTitle = document.createElement('div');
  opTitle.className = 'op-row-operation-title';
  // Operations added from the techs app carry only Short_Description.
  const shortDesc = (op.Short_Description_DM || op.Description || op.Short_Description || '').split(':')[0].split('(')[0];
  opTitle.innerHTML = `<span class="op-row-opcode-inline">${escapeHTML(op.OpCode || '')}</span>${escapeHTML(shortDesc)}${opRowHoldSublineHtml(op)}${opRowChecklistHtml(op)}`;
  addOpRowPaint(opTitle, op);

  // Plain text, no avatar — matches the Work-Orders expand-panel table
  // spec exactly (design-files §3a), which is text-only in this column;
  // the avatar treatment is specific to the Operations-tab flat table (§3b).
  const primaryTech = splitTechs(op.Techs)[0] || '';
  const tech = document.createElement('button');
  tech.type = 'button';
  tech.className = 'op-row-tech op-tech-select noPrint';
  // Two spans, not one flex container: the label alone gets the ellipsis truncation, so a long
  // name can't push the chevron out of view or get corrupted by overflow:hidden clipping the
  // wrong end (text-overflow:ellipsis doesn't apply correctly inside a flex box).
  tech.innerHTML = `<span class="op-row-tech-label">${escapeHTML(techLabel(primaryTech) || 'Unassigned')}</span><span class="op-row-tech-chevron">&#9662;</span>`;
  if (!primaryTech) tech.classList.add('op-tech-unassigned');
  tech.addEventListener('click', (e) => {
    e.stopPropagation();
    populateTechSelectModal(e, 'table');
  });

  const scheduleInput = document.createElement('input');
  scheduleInput.type = 'date';
  scheduleInput.className = 'schedule-date-info op-row-schedule';
  scheduleInput.value = op.Schedule_Date ? op.Schedule_Date : '';
  if (!op.Schedule_Date) scheduleInput.classList.add('schedule-date-unset');
  wireScheduleDateInput(scheduleInput, (date) => {
    const opRow = scheduleInput.closest('.op-table-data-row');
    const jobId = opRow.getAttribute('data-id');
    shell.updateJobInfo(jobId, 'Schedule_Date', date);
    shell.updateJobRow(jobId, 'Schedule_Date', date);
    op.Schedule_Date = date;
    refreshOpRowSeverity(opRow, op);
    refreshWorkOrderRow(op.Work_Order);
    applyFilters();
    applySorting();
    shell.searchCall();
    shell.searchJobs();
    updateWeekView();
  });

  const hrs = document.createElement('span');
  hrs.className = 'op-row-hrs';
  hrs.textContent = op.Estimated_Hours ? parseFloat(op.Estimated_Hours).toFixed(1) : '';

  // Compact priority editor — no visible label (unlike the old "Priority: ▾"
  // block), but this is still the only place in the app Priority can be
  // changed, so it stays as a real (small) control rather than disappearing.
  const prioritySelect = document.createElement('select');
  prioritySelect.className = 'priority-select op-row-priority';
  prioritySelect.title = 'Priority';
  [['', '—'], ['Low', 'L'], ['High', 'H']].forEach(([value, label]) => {
    const option = document.createElement('option');
    option.value = value;
    option.textContent = label;
    prioritySelect.appendChild(option);
  });
  if (op.Priority) prioritySelect.value = op.Priority;
  prioritySelect.addEventListener('click', (e) => e.stopPropagation());
  prioritySelect.addEventListener('change', async function () {
    const id = this.closest('.op-table-data-row').getAttribute('data-id');
    const priority = this.value;
    await shell.updateJobInfo(id, 'Priority', priority);
    await shell.updateJobRow(id, 'Priority', priority);
  });

  const statusPill = document.createElement('span');
  statusPill.className = `status-info-p wo-tone-${OP_ROW_PILL_TONE[severity] || 'slate'} op-row-status op-row-status-editable`;
  statusPill.textContent = OP_ROW_PILL_LABEL[severity] || (op.Status || '');
  // Unlike Tech/Date/Priority, which are obviously-interactive controls in this same row, Status
  // was plain text with no visual cue that clicking anywhere on the row opens the editor where
  // it's actually changed — this adds a hover affordance + tooltip so it doesn't read as inert.
  statusPill.title = 'Click anywhere on this row to change status';

  const jobNotesP = document.createElement('button');
  jobNotesP.type = 'button';
  jobNotesP.className = 'job-notes-button op-row-notes-btn';
  jobNotesP.innerHTML = `<img src="/assets/note-icon.png" alt="Notes" width="14" height="14">`;
  if (op.Notes) {
    jobNotesP.classList.add('op-row-notes-btn--has-notes');
    jobNotesP.querySelector('img').classList.add('has-notes');
  }

  jobNotesP.addEventListener('click', async (e) => {
    e.stopPropagation();
    await openNotesPanel(row.getAttribute('data-id'), jobNotesP);
  });

  // Single-line grid. Measured against real data at a plausible window
  // width: with OpCode and the notes button each holding their own grid
  // track, the title track measured ~99px in the live app — barely above
  // its floor, ellipsizing almost every title ("WINTERIZE …", "PUMP
  // HOLDI…"). OpCode is now inlined into the title cell (above) and the
  // notes button is taken out of grid flow entirely (absolutely positioned,
  // shown on hover — see CSS), freeing ~62px for the title.
  row.appendChild(dot);
  row.appendChild(opTitle);
  row.appendChild(tech);
  row.appendChild(scheduleInput);
  row.appendChild(hrs);
  row.appendChild(prioritySelect);
  row.appendChild(statusPill);
  row.appendChild(jobNotesP);
  syncOpRowBillButton(row, op);

  return row;
}

export function opProgressHeaderHtml(agg) {
  return `
        <div class="op-progress-summary">
          <span class="op-progress-title">Operations</span>
          <span class="op-progress-detail">${agg.loggedHours.toFixed(1)} hrs logged · ${agg.doneCount} of ${agg.activeOps.length} done</span>
        </div>
        <div class="op-progress-bar"><div class="op-progress-bar-fill" style="width:${agg.pct}%;"></div></div>
      `;
}

export function opSummaryChipsHtml(agg) {
  const hours = (ops) => ops.reduce((s, op) => s + (parseFloat(op.Estimated_Hours) || 0), 0);
  const runningCount = agg.runningOps.length;
  const scheduledCount = agg.scheduledOps.length;
  return `
        <span class="op-summary-chip op-summary-chip--done">${agg.doneCount} done · ${hours(agg.doneOps).toFixed(1)} hrs</span>
        ${runningCount ? `<span class="op-summary-chip op-summary-chip--running">${runningCount} running · ${hours(agg.runningOps).toFixed(1)} hrs</span>` : ''}
        ${scheduledCount ? `<span class="op-summary-chip op-summary-chip--scheduled">${scheduledCount} scheduled · ${hours(agg.scheduledOps).toFixed(1)} hrs</span>` : ''}
        ${agg.unscheduled.count ? `<span class="op-summary-chip op-summary-chip--unscheduled">${agg.unscheduled.count} unscheduled · ${agg.unscheduled.hours.toFixed(1)} hrs</span>` : ''}
        <span class="op-summary-sort-note">Sorted by scheduled date</span>
      `;
}

// Live (docs/live-updates-plan.md): the expanded operations list under a work order follows other
// users. A changed operation's row is rebuilt on its own (unless the user is using a control in it),
// a new one is slotted in by date, a removed / declined / moved one goes; the summary updates in
// place. Nothing else in the panel is touched.
export function syncOpenOperationsPanel(workOrder) {
  if (workOrder == null) return;
  const container = document.querySelector(`.operation-container-row[data-workorder="${CSS.escape(String(workOrder))}"]`);
  const table = container?.querySelector('#operations-table');
  if (!table) return;
  const ops = (shell.jobsByWorkOrder?.get(workOrder) || shell.jobsByWorkOrder?.get(String(workOrder)) || [])
    .filter(op => op.Category?.toString() !== 'Declined' && op.Status?.toString() !== 'Declined');
  const byId = new Map(ops.map(op => [String(op.Job_ID || op.id), op]));
  const changed = [];
  table.querySelectorAll('.op-table-data-row').forEach(row => {
    const id = row.getAttribute('data-id');
    const op = byId.get(id);
    if (!op) { row.remove(); return; }
    byId.delete(id);
    if (row.dataset.liveSig === JSON.stringify(op)) return; // (data-jobInfo is kept current elsewhere; this is what's drawn)
    if (row.contains(document.activeElement)) return; // mid-edit of its date / priority
    const fresh = buildOpTableRow(op);
    // Same data, drawn the same: keep the row (its copy of the data may just carry other fields).
    const classes = (el) => [...el.classList].filter(c => c !== 'selectedRow' && c !== 'live-changed').sort().join(' ');
    if (fresh.innerHTML === row.innerHTML && classes(fresh) === classes(row)) { row.dataset.liveSig = fresh.dataset.liveSig; return; }
    if (row.classList.contains('selectedRow')) fresh.classList.add('selectedRow');
    row.replaceWith(fresh);
    changed.push(fresh);
  });
  const dateOf = (op) => op.Schedule_Date ? new Date(op.Schedule_Date).getTime() : Infinity;
  byId.forEach(op => {
    const fresh = buildOpTableRow(op);
    const before = [...table.querySelectorAll('.op-table-data-row')].find(row => {
      let other = null;
      try { other = JSON.parse(row.getAttribute('data-jobInfo') || 'null'); } catch (_) { /* keep looking */ }
      return other && dateOf(other) > dateOf(op);
    });
    table.insertBefore(fresh, before || null);
    changed.push(fresh);
  });
  const agg = aggregateWorkOrder(ops);
  const header = container.querySelector('.op-progress-header');
  if (header) patchHTML(header, opProgressHeaderHtml(agg));
  const chips = container.querySelector('.op-summary-chips');
  if (chips) patchHTML(chips, opSummaryChipsHtml(agg));
  shell.glowJobRows(changed);
}

export function opTableList(ops, operationsTable) {

  // Column header, matching the compact rows' grid exactly (design-files
  // §3a) — with the row labels gone (no more "Start:"/"Scheduled:"/
  // "Priority:" text), a header is what makes the columns legible.
  const header = document.createElement('div');
  header.className = 'op-table-header-row';
  header.innerHTML = `
    <span>Operation</span>
    <span>Tech</span>
    <span>Scheduled</span>
    <span class="op-row-hrs">Hrs</span>
    <span>Pri</span>
    <span class="op-row-status">Status</span>
    <span></span>
  `;
  operationsTable.appendChild(header);

  // Schedule_Date ascending, nulls last, then OpCode — README explicitly
  // overrides the "yard's intended work order" language from the prototype.
  ops.sort((a, b) => {
    const aTime = a.Schedule_Date ? new Date(a.Schedule_Date).getTime() : Infinity;
    const bTime = b.Schedule_Date ? new Date(b.Schedule_Date).getTime() : Infinity;
    if (aTime !== bTime) return aTime - bTime;
    return String(a.OpCode ?? '').localeCompare(String(b.OpCode ?? ''), undefined, { numeric: true });
  });

  ops.forEach(function (r) {
    if (r.Status?.toString() === 'Declined') return;
    operationsTable.appendChild(buildOpTableRow(r));
  });

};

// Single-row insert after addOperation() — delegates to buildOpTableRow()
// (see comment above opTableList()) instead of maintaining a second,
// independently-drifting row layout.
export async function createNewOpRow(opData) {
  if (opData.Status?.toString() === 'Declined') return null;
  return buildOpTableRow(opData);
}
